import React, { useState, useRef, useEffect } from 'react'
import { Row, Col, Badge, Image, Button, Nav } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { uploadFile } from '../../../actions/productsActions'
import getProxiedImageUrl from '../../../helpers/imageProxy'

const ProductViewModal = ({ show, product, t, onAiFill, aiFillLoading, onEdit, onSaveDocuments }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [activeTab, setActiveTab] = useState('summary')

    const [localDocuments, setLocalDocuments] = useState(product?.documents || [])
    const [localImages, setLocalImages] = useState(product?.images || [])
    const [localMainImage, setLocalMainImage] = useState(product?.mainImage || '')
    const [localPdfUrl, setLocalPdfUrl] = useState(product?.pdfUrl || '')
    const [uploadingDoc, setUploadingDoc] = useState(false)
    const [uploadingImg, setUploadingImg] = useState(false)
    const [uploadingPdf, setUploadingPdf] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)
    const docInputRef = useRef(null)
    const imgInputRef = useRef(null)
    const pdfInputRef = useRef(null)

    useEffect(() => {
        setLocalDocuments(product?.documents || [])
        setLocalImages(product?.images || [])
        setLocalMainImage(product?.mainImage || '')
        setLocalPdfUrl(product?.pdfUrl || '')
        setHasChanges(false)
    }, [product?._id, product?.pdfUrl, product?.mainImage, product?.images, product?.documents])

    if (!product) {
        return null
    }

    const handleHistory = () => {
        navigate(`/admin/product-history/${product._id}`)
    }

    const prices = product.erpCache?.prices || []
    const tariffNames = ['Tarifa general', 'Tarifa profesional', 'Tarifa especial']

    const getDocIcon = (type) => {
        const t = String(type || '').toLowerCase()
        if (t.includes('pdf')) return 'fas fa-file-pdf text-danger'
        if (t.includes('image') || t.includes('img')) return 'fas fa-file-image text-primary'
        if (t.includes('xls') || t.includes('excel')) return 'fas fa-file-excel text-success'
        return 'fas fa-file text-secondary'
    }

    const handleDocUpload = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        setUploadingDoc(true)
        try {
            const url = await dispatch(uploadFile(file, 'document'))
            setLocalDocuments(prev => [...prev, { url, name: file.name, type: file.type }])
            setHasChanges(true)
        } catch (err) {
            console.error('Error uploading document:', err)
        } finally {
            setUploadingDoc(false)
            if (docInputRef.current) docInputRef.current.value = ''
        }
    }

    const handlePdfUpload = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        setUploadingPdf(true)
        try {
            const url = await dispatch(uploadFile(file, 'document'))
            setLocalPdfUrl(url)
            setHasChanges(true)
        } catch (err) {
            console.error('Error uploading ficha técnica:', err)
        } finally {
            setUploadingPdf(false)
            if (pdfInputRef.current) pdfInputRef.current.value = ''
        }
    }

    const removePdf = () => {
        setLocalPdfUrl('')
        setHasChanges(true)
    }

    const handleImgUpload = async (e) => {
        const files = Array.from(e.target.files || [])
        if (files.length === 0) return

        setUploadingImg(true)
        try {
            const uploads = files.map(file => dispatch(uploadFile(file, 'image')))
            const urls = await Promise.all(uploads)
            const newImages = files.map((file, i) => ({ url: urls[i], name: file.name }))
            setLocalImages(prev => [...prev, ...newImages])
            setHasChanges(true)
        } catch (err) {
            console.error('Error uploading images:', err)
        } finally {
            setUploadingImg(false)
            if (imgInputRef.current) imgInputRef.current.value = ''
        }
    }

    const removeDocument = (index) => {
        setLocalDocuments(prev => prev.filter((_, i) => i !== index))
        setHasChanges(true)
    }

    const removeImage = (index) => {
        const newImages = localImages.filter((_, i) => i !== index)
        setLocalImages(newImages)
        if (newImages.length === 0 && product.imageUrl) {
            setLocalMainImage(product.imageUrl)
        }
        setHasChanges(true)
    }

    const setMainImage = (url) => {
        setLocalMainImage(url)
        setHasChanges(true)
    }

    const handleSaveDocs = async () => {
        if (!onSaveDocuments) return
        await onSaveDocuments({
            documents: localDocuments,
            images: localImages,
            mainImage: localMainImage,
            pdfUrl: localPdfUrl,
        })
        setHasChanges(false)
    }

    const hasImages = localImages.length > 0
    const hasAiImage = !!product.imageUrl
    const hasDocuments = localDocuments.length > 0
    const hasPdf = !!localPdfUrl
    const parseSources = (sources) => {
        if (Array.isArray(sources)) return sources.filter(Boolean)
        if (typeof sources === 'string' && sources.trim()) return sources.split(',').map(s => s.trim()).filter(Boolean)
        return []
    }

    const sourceList = parseSources(product.sources)
    const hasSources = sourceList.length > 0

    return (
        <div className="product-detail">
            {/* Product Header */}
            <div className="product-detail-header">
                <div className="product-detail-img-wrap">
                    {localMainImage ? (
                        <Image src={getProxiedImageUrl(localMainImage)} referrerPolicy="no-referrer" className="product-detail-img" />
                    ) : hasImages ? (
                        <Image src={getProxiedImageUrl(localImages[0].url)} referrerPolicy="no-referrer" className="product-detail-img" />
                    ) : hasAiImage ? (
                        <Image src={getProxiedImageUrl(product.imageUrl)} referrerPolicy="no-referrer" className="product-detail-img" />
                    ) : (
                        <div className="product-detail-img-placeholder">
                            <i className="fas fa-box"></i>
                        </div>
                    )}
                </div>
                <div className="product-detail-info">
                    {product.group && (
                        <Badge className="product-detail-badge">{product.group}</Badge>
                    )}
                    <h5 className="product-detail-name">{product.name || product.description || '-'}</h5>
                    {product.reference && (
                        <div className="product-detail-ref">Ref. {product.reference}</div>
                    )}
                </div>
            </div>

            {/* Action buttons */}
            <div className="product-detail-actions">
                <Button variant="secondary" size="sm" onClick={handleHistory}>
                    <i className="fas fa-clock-rotate-left me-1"></i>
                    <span className="d-none d-sm-inline">{t('admin.products.history')}</span>
                </Button>
                {onEdit && (
                    <Button variant="primary" size="sm" onClick={() => onEdit(product)}>
                        <i className="fas fa-edit me-1"></i>
                        <span className="d-none d-sm-inline">{t('global.edit')}</span>
                    </Button>
                )}
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={onAiFill}
                    disabled={aiFillLoading}
                >
                    <i className={`fas fa-robot me-1 ${aiFillLoading ? 'fa-spin' : ''}`}></i>
                    <span className="d-none d-sm-inline">{aiFillLoading ? t('admin.products.aiFilling') : t('admin.products.aiFill')}</span>
                </Button>
            </div>

            {/* Tabs */}
            <div className="product-detail-tabs">
                <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                    <Nav.Item>
                        <Nav.Link eventKey="summary">
                            <i className="fas fa-circle-info me-1"></i>
                            {t('admin.products.details') || 'Resumen'}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="attributes">
                            <i className="fas fa-list-check me-1"></i>
                            {t('admin.products.attributes') || 'Atributos'}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="prices">
                            <i className="fas fa-tag me-1"></i>
                            {t('admin.products.prices') || 'Precios'}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="images">
                            <i className="fas fa-image me-1"></i>
                            Imágenes
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="documents">
                            <i className="fas fa-file-pdf me-1"></i>
                            Documentos
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="sources">
                            <i className="fas fa-link me-1"></i>
                            Fuentes
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <div className="product-detail-tab-content">
                    {/* TAB: Resumen */}
                    {activeTab === 'summary' && (
                        <div>
                            <div className="product-section-card">
                                <div className="product-section-title">
                                    <i className="fas fa-circle-info"></i>
                                    {t('admin.products.details')}
                                </div>
                                <div className="product-info-grid">
                                    <div className="product-info-item">
                                        <span className="product-info-label">{t('admin.products.reference')}</span>
                                        <span className="product-info-value">{product.reference || '-'}</span>
                                    </div>
                                    <div className="product-info-item">
                                        <span className="product-info-label">{t('admin.products.erpReference')}</span>
                                        <span className="product-info-value">{product.erpReference || '-'}</span>
                                    </div>
                                    <div className="product-info-item">
                                        <span className="product-info-label">{t('admin.products.brand')}</span>
                                        <span className="product-info-value">{product.brand || '-'}</span>
                                    </div>
                                    <div className="product-info-item">
                                        <span className="product-info-label">{t('admin.products.group')}</span>
                                        <span className="product-info-value">{product.group || '-'}</span>
                                    </div>
                                    <div className="product-info-item">
                                        <span className="product-info-label">{t('admin.products.subgroup')}</span>
                                        <span className="product-info-value">{product.subgroup || '-'}</span>
                                    </div>
                                </div>
                            </div>

                            {product.description && (
                                <div className="product-section-card">
                                    <div className="product-section-title">
                                        <i className="fas fa-align-left"></i>
                                        {t('admin.products.description')}
                                    </div>
                                    <p className="product-description-text">{product.description}</p>
                                </div>
                            )}

                            {product.departments?.length > 0 && (
                                <div className="product-section-card">
                                    <div className="product-section-title">
                                        <i className="fas fa-building"></i>
                                        {t('admin.products.departments')}
                                    </div>
                                    <div className="product-badges-wrap">
                                        {product.departments.map((dept, i) => (
                                            <Badge key={i} className="product-tag product-tag-dark">{dept}</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {product.synonyms?.length > 0 && (
                                <div className="product-section-card">
                                    <div className="product-section-title">
                                        <i className="fas fa-tags"></i>
                                        {t('admin.products.synonyms')}
                                    </div>
                                    <div className="product-badges-wrap">
                                        {product.synonyms.map((syn, i) => (
                                            <Badge key={i} className="product-tag product-tag-info">{syn}</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {product.families?.length > 0 && product.families.some(f => f) && (
                                <div className="product-section-card">
                                    <div className="product-section-title">
                                        <i className="fas fa-layer-group"></i>
                                        {t('admin.products.families')}
                                    </div>
                                    <div className="product-badges-wrap">
                                        {product.families.filter(f => f).map((fam, i) => (
                                            <Badge key={i} className="product-tag product-tag-warning">{fam}</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="product-section-card">
                                <div className="product-section-title">
                                    <i className="fas fa-clock"></i>
                                    {t('admin.products.metadata')}
                                </div>
                                <div className="product-info-grid">
                                    <div className="product-info-item">
                                        <span className="product-info-label">{t('admin.products.createdAt')}</span>
                                        <span className="product-info-value">
                                            {product.createdAt ? new Date(product.createdAt).toLocaleString() : '-'}
                                        </span>
                                    </div>
                                    <div className="product-info-item">
                                        <span className="product-info-label">{t('admin.products.updatedAt')}</span>
                                        <span className="product-info-value">
                                            {product.updatedAt ? new Date(product.updatedAt).toLocaleString() : '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: Atributos */}
                    {activeTab === 'attributes' && (
                        <div>
                            <div className="product-section-card">
                                <div className="product-section-title">
                                    <i className="fas fa-list-check"></i>
                                    {t('admin.products.attributes')}
                                </div>

                                {product.attributes && Array.isArray(product.attributes) && product.attributes.length > 0 ? (
                                    <div className="product-attr-table">
                                        {product.attributes.map((attr, index) => (
                                            <div className="product-attr-row" key={index}>
                                                <span className="product-attr-key">{attr.label || attr.key}</span>
                                                <span className="product-attr-val">
                                                    {attr.value === true ? t('global.yes')
                                                        : attr.value === false ? t('global.no')
                                                        : typeof attr.value === 'object' ? JSON.stringify(attr.value)
                                                        : String(attr.value)}
                                                    {attr.unit ? ` ${attr.unit}` : ''}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="product-empty">{t('admin.products.noAttributes')}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB: Precios */}
                    {activeTab === 'prices' && (
                        <div>
                            {prices.length > 0 ? (
                                <>
                                    <div className="product-section-card">
                                        <div className="product-section-title">
                                            <i className="fas fa-tag"></i>
                                            Precios por tarifa
                                        </div>
                                    </div>

                                    {prices.map((priceValue, index) => {
                                        const tariffIcons = ['fas fa-building', 'fas fa-box', 'fas fa-tag', 'fas fa-star']
                                        const icon = tariffIcons[index % tariffIcons.length]
                                        const num = Number(priceValue) || 0

                                        return (
                                            <div className="product-price-card" key={index}>
                                                <div className="product-price-card-header">
                                                    <span className="product-price-name">
                                                        Tarifa {index + 1}
                                                    </span>
                                                    <i className={`${icon} product-price-tariff-icon`}></i>
                                                    <span className="product-price-desc">
                                                        {tariffNames[index] || `Tarifa ${index + 1}`}
                                                    </span>
                                                </div>
                                                <div className="product-price-card-body">
                                                    <div className="product-price-left">
                                                        <span className="product-price-label">Precio</span>
                                                        <div className="product-price-main">
                                                            <span className="product-price-value">
                                                                {num > 0 ? num.toFixed(3).replace('.', ',') : '-'} &euro;
                                                            </span>
                                                        </div>
                                                        <span className="product-price-tax">IVA no incluido</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </>
                            ) : (
                                <div className="product-section-card">
                                    <div className="product-empty">
                                        <i className="fas fa-tag me-2"></i>
                                        No hay precios disponibles
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: Imágenes */}
                    {activeTab === 'images' && (
                        <div>
                            <div className="product-section-card">
                                <div className="product-section-title">
                                    <i className="fas fa-image"></i>
                                    Imágenes
                                    {hasImages && (
                                        <Badge className="product-count-badge">{localImages.length}</Badge>
                                    )}
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        className="ms-auto"
                                        onClick={() => imgInputRef.current?.click()}
                                        disabled={uploadingImg}
                                    >
                                        <i className={`fas fa-upload me-1`}></i>
                                        {uploadingImg ? 'Subiendo...' : 'Subir imagen'}
                                    </Button>
                                    <input
                                        ref={imgInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={handleImgUpload}
                                    />
                                </div>

                                {hasImages ? (
                                    <Row className="g-2">
                                        {localImages.map((image, index) => {
                                            const isMain = localMainImage === image.url
                                            return (
                                                <Col xs={4} sm={3} key={index}>
                                                    <div
                                                        className="product-thumb-wrap"
                                                        style={{
                                                            position: 'relative',
                                                            border: isMain ? '2px solid #0d6efd' : '2px solid transparent',
                                                            borderRadius: '6px',
                                                        }}
                                                    >
                                                        <Image src={getProxiedImageUrl(image.url)} referrerPolicy="no-referrer" className="product-thumb-img" />
                                                        <div style={{ position: 'absolute', top: '4px', right: '4px', display: 'flex', gap: '2px' }}>
                                                            <Button
                                                                variant={isMain ? 'primary' : 'outline-secondary'}
                                                                size="sm"
                                                                style={{ padding: '2px 6px', fontSize: '0.7rem' }}
                                                                onClick={() => setMainImage(image.url)}
                                                                title="Imagen principal"
                                                            >
                                                                <i className="fas fa-star"></i>
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                style={{ padding: '2px 6px', fontSize: '0.7rem' }}
                                                                onClick={() => removeImage(index)}
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Col>
                                            )
                                        })}
                                    </Row>
                                ) : hasAiImage ? (
                                    <div>
                                        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                                            <i className="fas fa-robot me-1"></i>
                                            Imagen obtenida por IA (no hay imágenes subidas)
                                        </p>
                                        <Image
                                            src={getProxiedImageUrl(product.imageUrl)}
                                            referrerPolicy="no-referrer"
                                            style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                                        />
                                    </div>
                                ) : (
                                    <div className="product-empty">
                                        <i className="fas fa-image me-2"></i>
                                        {uploadingImg ? 'Subiendo imagen...' : 'No hay imágenes disponibles'}
                                    </div>
                                )}
                            </div>

                            {hasChanges && (
                                <div className="d-flex justify-content-end mt-3">
                                    <Button variant="primary" onClick={handleSaveDocs}>
                                        <i className="fas fa-save me-1"></i>
                                        Guardar cambios
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: Documentos (Ficha técnica) */}
                    {activeTab === 'documents' && (
                        <div>
                            <div className="product-section-card">
                                <div className="product-section-title">
                                    <i className="fas fa-file-pdf"></i>
                                    Ficha técnica
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        className="ms-auto"
                                        onClick={() => pdfInputRef.current?.click()}
                                        disabled={uploadingPdf}
                                    >
                                        <i className={`fas fa-upload me-1`}></i>
                                        {uploadingPdf ? 'Subiendo...' : 'Subir ficha técnica'}
                                    </Button>
                                    <input
                                        ref={pdfInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        style={{ display: 'none' }}
                                        onChange={handlePdfUpload}
                                    />
                                </div>

                                {hasPdf ? (
                                    <div className="d-flex align-items-center gap-2">
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            href={localPdfUrl}
                                            target="_blank"
                                        >
                                            <i className="fas fa-file-pdf me-1"></i>
                                            Abrir ficha técnica
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={removePdf}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="product-empty">
                                        <i className="fas fa-file-pdf me-2"></i>
                                        {uploadingPdf ? 'Subiendo ficha técnica...' : 'No hay ficha técnica. Sube un PDF.'}
                                    </div>
                                )}
                            </div>

                            <div className="product-section-card">
                                <div className="product-section-title">
                                    <i className="fas fa-folder-open"></i>
                                    Documentos
                                    {hasDocuments && (
                                        <Badge className="product-count-badge">{localDocuments.length}</Badge>
                                    )}
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        className="ms-auto"
                                        onClick={() => docInputRef.current?.click()}
                                        disabled={uploadingDoc}
                                    >
                                        <i className={`fas fa-upload me-1`}></i>
                                        {uploadingDoc ? 'Subiendo...' : 'Subir documento'}
                                    </Button>
                                    <input
                                        ref={docInputRef}
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={handleDocUpload}
                                    />
                                </div>

                                {hasDocuments ? (
                                    <div className="product-doc-list">
                                        {localDocuments.map((doc, index) => (
                                            <div className="product-doc-item" key={index}>
                                                <div className="product-doc-icon">
                                                    <i className={getDocIcon(doc.type)}></i>
                                                </div>
                                                <div className="product-doc-info">
                                                    <span className="product-doc-name">{doc.name}</span>
                                                    <span className="product-doc-type">{doc.type || 'Doc'}</span>
                                                </div>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    href={doc.url}
                                                    target="_blank"
                                                    className="product-doc-download me-1"
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => removeDocument(index)}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="product-empty">
                                        <i className="fas fa-folder-open me-2"></i>
                                        {uploadingDoc ? 'Subiendo documento...' : 'No hay documentos. Sube uno nuevo.'}
                                    </div>
                                )}
                            </div>

                            {hasChanges && (
                                <div className="d-flex justify-content-end mt-3">
                                    <Button variant="primary" onClick={handleSaveDocs}>
                                        <i className="fas fa-save me-1"></i>
                                        Guardar cambios
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: Fuentes */}
                    {activeTab === 'sources' && (
                        <div>
                            <div className="product-section-card">
                                <div className="product-section-title">
                                    <i className="fas fa-link"></i>
                                    Fuentes de información
                                </div>

                                {hasSources ? (
                                    <div className="product-sources-list">
                                        {sourceList.map((source, index) => (
                                            <div key={index} className="product-source-item">
                                                <i className="fas fa-external-link-alt me-2"></i>
                                                {source.startsWith('http') ? (
                                                    <a href={source} target="_blank" rel="noopener noreferrer">{source}</a>
                                                ) : (
                                                    <span>{source}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="product-empty">
                                        <i className="fas fa-link me-2"></i>
                                        No hay fuentes disponibles. Usa "Rellenar con IA" para obtener datos con referencias.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ProductViewModal
